import React, { useState } from "react";
import "./LandingPage.css";
import SignUpModal from "../../modals/SignUpModal";
import SignInModal from "../../modals/SignInModal";
import { useAuth } from '../../contexts/authContext';

const LandingPage = () => {
  // Local modal state for public layout
  const { login } = useAuth();
  const [showSignUp, setShowSignUp] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);

  const openSignUp = () => setShowSignUp(true);
  const openSignIn = () => setShowSignIn(true);
  const switchToSignIn = () => {
    setShowSignUp(false);
    setShowSignIn(true);
  };
  const switchToSignUp = () => {
    setShowSignIn(false);
    setShowSignUp(true);
  };

  return (
    <div className="landing-page">
      <section className="hero-section">
        <h1>
          Welcome to our social language learning platform!
        </h1>
        <p>
          Learn languages through interactive lessons, study groups, and virtual
          classrooms designed to create meaningful connections with fellow learners
          around the world.
        </p>
        <div className="hero-buttons">
          {/* Open Sign Up modal */}
          <button className="get-started" onClick={openSignUp}>
            Get Started
          </button>
          {/* Replace onExploreCoursesClick with a local function */}
          <button className="explore-courses-btn" onClick={openSignIn}>
            Explore Courses
          </button>
        </div>
      </section>

      {/* Language Learning Reimagined Section */}
      <section className="reimagined-section">
        <h2>Language Learning Reimagined</h2>
        <p>
          At VirtuLingu Club, we believe that language acquisition is fundamentally a social
          process. Our platform is built on the principles of connectivism, recognizing that
          meaningful learning happens through the connections we form with others.
        </p>
      </section>

      {/* Learn Through Connections Section */}
      <section className="connections-section">
        <h2>Learn Through Connections</h2>
        <p>
          Experience the power of connectivism - the theory that emphasizes that learning thrives
          through the meaningful connections we forge with others. Our platform is designed to foster these
          connections, creating a vibrant network of language learners and native speakers.
        </p>
        
        <div className="connections-diagram">
          <div className="connection-node center">
            <span>You</span>
          </div>
          <div className="connection-node top">
            <span>Native Speakers</span>
          </div>
          <div className="connection-node right">
            <span>Tutors</span>
          </div>
          <div className="connection-node bottom-right">
            <span>Fellow Learners</span>
          </div>
          <div className="connection-node bottom-left">
            <span>Study Groups</span>
          </div>
          <svg className="connections-lines" viewBox="0 0 300 300">
            <line x1="150" y1="150" x2="150" y2="50" />
            <line x1="150" y1="150" x2="250" y2="150" />
            <line x1="150" y1="150" x2="220" y2="220" />
            <line x1="150" y1="150" x2="80" y2="220" />
          </svg>
        </div>
      </section>

      <section className="platform-section">
        <h2>What You Can Do on VirtuLingu Club</h2>
        <p>
          Join a community built for real practice. Meet people who share your goals, learn together, and use
          communication tools designed for language exchange.
        </p>

        <div className="platform-grid">
          <div className="platform-card">
            <div className="platform-icon">P</div>
            <h3>Find your language exchange partner</h3>
            <p>
              Connect with learners and native speakers who match your target language and interests so practice feels natural.
            </p>
          </div>

          <div className="platform-card">
            <div className="platform-icon">G</div>
            <h3>Create and join study groups</h3>
            <p>
              Build consistency with group chats, shared goals, and discussion spaces that keep you practicing week after week.
            </p>
          </div>

          <div className="platform-card">
            <div className="platform-icon">E</div>
            <h3>Participate in language events</h3>
            <p>
              Join scheduled sessions like conversation circles and community meetups where you can use your language live.
            </p>
          </div>
        </div>

        <div className="tools-section">
          <h3 className="tools-title">Communication tools for practice</h3>
          <div className="tools-grid">
            <div className="tool-card">
              <span className="tool-label">Messaging</span>
              <p>Direct messages and group chat to keep conversations going daily.</p>
            </div>
            <div className="tool-card">
              <span className="tool-label">Audio & Video Calls</span>
              <p>Talk face-to-face to build confidence with real-time listening and speaking.</p>
            </div>
            <div className="tool-card">
              <span className="tool-label">VirtualCafe Rooms</span>
              <p>Drop into live rooms to practice with multiple people in an immersive setting.</p>
            </div>
            <div className="tool-card">
              <span className="tool-label">Study Threads</span>
              <p>Share resources, ask questions, and collaborate around specific topics.</p>
            </div>
          </div>

          <div className="practice-list">
            <div className="practice-split">
              <div className="practice-block">
                <h4 className="practice-title">VirtualCafe: live practice toolkit</h4>
                <ul className="practice-items">
                  <li>
                    <span className="practice-label">Room layouts & focus modes</span>
                    <span className="practice-text">Switch between grid, spotlight, presentation, and picture-in-picture layouts to match the activity.</span>
                  </li>
                  <li>
                    <span className="practice-label">Screen share</span>
                    <span className="practice-text">Share your screen for guided lessons, presentations, and real-world practice.</span>
                  </li>
                  <li>
                    <span className="practice-label">Shared media player</span>
                    <span className="practice-text">Watch and discuss videos together for listening practice and conversation prompts.</span>
                  </li>
                  <li>
                    <span className="practice-label">Collaborative whiteboard</span>
                    <span className="practice-text">Work through vocabulary, grammar, and prompts together in real time.</span>
                  </li>
                  <li>
                    <span className="practice-label">Shared documents & annotation</span>
                    <span className="practice-text">Open PDFs and annotate together for reading practice, lessons, and study sessions.</span>
                  </li>
                  <li>
                    <span className="practice-label">Chat & participants sidebar</span>
                    <span className="practice-text">Keep the conversation organized with in-room chat, participant lists, and quick coordination.</span>
                  </li>
                </ul>
              </div>

              <div className="practice-block">
                <h4 className="practice-title">More ways to learn on VirtuLingu</h4>
                <ul className="practice-items">
                  <li>
                    <span className="practice-label">Discover people, groups, and events</span>
                    <span className="practice-text">Browse communities that match your target language, goals, and level.</span>
                  </li>
                  <li>
                    <span className="practice-label">Study groups with resources</span>
                    <span className="practice-text">Use discussion forums, media, and files to keep learning structured between live sessions.</span>
                  </li>
                  <li>
                    <span className="practice-label">Language events + event rooms</span>
                    <span className="practice-text">Join scheduled sessions and practice live in VirtualCafe event rooms.</span>
                  </li>
                  <li>
                    <span className="practice-label">Community posts & LinguaSnaps</span>
                    <span className="practice-text">Share updates, resources, and short-form language clips to stay consistent.</span>
                  </li>
                  <li>
                    <span className="practice-label">Academy: explore language courses</span>
                    <span className="practice-text">Follow structured lessons and tools that support your learning plan.</span>
                  </li>
                  <li>
                    <span className="practice-label">eBookstore</span>
                    <span className="practice-text">Discover and build a library of reading resources to expand your vocabulary and comprehension.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="cta-section">
        <h2>Ready to Start Your Language Journey?</h2>
        <p>Join thousands of learners who are already expanding their horizons with VirtuLingu Club.</p>
        <button className="get-started-large" onClick={openSignUp}>Get Started Today</button>
      </div>

      {/* Auth Modals */}
      <SignUpModal 
        open={showSignUp} 
        onClose={() => setShowSignUp(false)} 
        onSignUp={() => setShowSignUp(false)} 
        onSwitchToSignIn={switchToSignIn}
      />
      <SignInModal 
        open={showSignIn} 
        onClose={() => setShowSignIn(false)} 
        onSignIn={async (email, password) => {
          try {
            await login({ email, password });
            setShowSignIn(false);
          } catch (e) {
            // Let modal show its own error via thrown message
            throw e;
          }
        }} 
        onSwitchToSignUp={switchToSignUp}
      />
    </div>
  );
};

export default LandingPage;